import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle, 
  XCircle, 
  Clock,
  Award,
  RotateCcw
} from "lucide-react";
import NavBar from "@/components/NavBar";
import { useNavigate } from "react-router-dom";

interface Question {
  id: string;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
  category: string;
}

interface ModuleData {
  id: string;
  title: string;
  description: string;
  caseType: 'soc' | 'obc' | 'fbc';
  caseElement: 'strategic' | 'economic' | 'commercial' | 'financial' | 'management';
  questions: Question[];
  timeLimit: number; // in minutes
  passRate: number; // percentage
}

// Sample module data
const sampleModule: ModuleData = {
  id: "soc-strategic-1",
  title: "SOC Strategic Case - Module 1: Problem Definition",
  description: "Understanding how to define problems and establish strategic context in a Strategic Outline Case",
  caseType: 'soc',
  caseElement: 'strategic',
  timeLimit: 30,
  passRate: 75,
  questions: [
    {
      id: "q1",
      question: "What is the primary purpose of the Strategic Case in a business case?",
      options: [
        "To calculate financial returns",
        "To make the case for change and establish strategic context",
        "To outline procurement strategies",
        "To detail implementation timelines"
      ],
      correctAnswer: 1,
      explanation: "The Strategic Case makes the case for change and establishes the strategic context for the proposal.",
      category: "Strategic Context"
    },
    {
      id: "q2", 
      question: "According to HM Treasury guidance, what should be the first step in developing a Strategic Case?",
      options: [
        "Identify potential solutions",
        "Calculate costs and benefits",
        "Define the problem or opportunity",
        "Engage with stakeholders"
      ],
      correctAnswer: 2,
      explanation: "The first step should be to clearly define the problem or opportunity that needs to be addressed.",
      category: "Problem Definition"
    },
    {
      id: "q3",
      question: "Which of the following is NOT typically included in a Strategic Case?",
      options: [
        "Spending objectives",
        "Detailed procurement timelines",
        "Strategic context",
        "Existing arrangements"
      ],
      correctAnswer: 1,
      explanation: "Detailed procurement timelines belong in the Commercial Case, not the Strategic Case.",
      category: "Strategic Content"
    },
    {
      id: "q4",
      question: "What does SMART stand for in the context of spending objectives?",
      options: [
        "Simple, Measurable, Achievable, Relevant, Time-bound",
        "Specific, Measurable, Achievable, Relevant, Time-bound", 
        "Strategic, Measurable, Affordable, Realistic, Targeted",
        "Specific, Meaningful, Actionable, Results-oriented, Testable"
      ],
      correctAnswer: 1,
      explanation: "SMART objectives are Specific, Measurable, Achievable, Relevant, and Time-bound.",
      category: "Objectives"
    },
    {
      id: "q5",
      question: "Which stakeholder analysis technique is recommended in the Green Book?",
      options: [
        "SWOT Analysis",
        "Force Field Analysis", 
        "Power-Interest Grid",
        "Value Stream Mapping"
      ],
      correctAnswer: 2,
      explanation: "The Power-Interest Grid is the stakeholder analysis technique recommended in HM Treasury guidance.",
      category: "Stakeholder Analysis"
    }
  ]
};

export default function TrainingModule() {
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<{ [key: string]: number }>({});
  const [isCompleted, setIsCompleted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(sampleModule.timeLimit * 60); // in seconds
  const [startTime] = useState(Date.now());

  const currentQuestion = sampleModule.questions[currentQuestionIndex];
  const totalQuestions = sampleModule.questions.length;
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  const handleAnswerSelect = (value: string) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [currentQuestion.id]: parseInt(value)
    });
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      completeModule();
    }
  };

  const previousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const completeModule = () => {
    setIsCompleted(true);
    setShowResults(true);
  };

  const calculateResults = () => {
    const correctAnswers = sampleModule.questions.filter(q => 
      selectedAnswers[q.id] === q.correctAnswer
    ).length;
    
    const score = Math.round((correctAnswers / totalQuestions) * 100);
    const passed = score >= sampleModule.passRate;
    const timeTaken = Math.round((Date.now() - startTime) / 1000 / 60); // in minutes

    return {
      correctAnswers,
      totalQuestions,
      score,
      passed,
      timeTaken
    };
  };

  const restartModule = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setIsCompleted(false);
    setShowResults(false);
    setTimeRemaining(sampleModule.timeLimit * 60);
  };

  const results = showResults ? calculateResults() : null;

  if (showResults && results) {
    return (
      <div className="min-h-screen bg-background">
        <NavBar />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardHeader className="text-center">
                <div className="mx-auto mb-4">
                  {results.passed ? (
                    <CheckCircle className="h-16 w-16 text-green-600" />
                  ) : (
                    <XCircle className="h-16 w-16 text-red-600" />
                  )}
                </div>
                <CardTitle className="text-2xl">
                  {results.passed ? "Congratulations!" : "Module Not Completed"}
                </CardTitle>
                <CardDescription>
                  {results.passed 
                    ? "You have successfully passed this training module" 
                    : `You need ${sampleModule.passRate}% to pass. Please try again.`}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-3 gap-4 text-center">
                  <div className="p-4 border rounded-lg">
                    <div className="text-2xl font-bold text-primary">{results.score}%</div>
                    <div className="text-sm text-muted-foreground">Final Score</div>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <div className="text-2xl font-bold text-primary">
                      {results.correctAnswers}/{results.totalQuestions}
                    </div>
                    <div className="text-sm text-muted-foreground">Correct Answers</div>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <div className="text-2xl font-bold text-primary">{results.timeTaken}m</div>
                    <div className="text-sm text-muted-foreground">Time Taken</div>
                  </div>
                </div>

                {results.passed && (
                  <div className="bg-green-50 dark:bg-green-950 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="h-5 w-5 text-green-600" />
                      <h3 className="font-semibold text-green-800 dark:text-green-200">Module Completed</h3>
                    </div>
                    <p className="text-green-700 dark:text-green-300 text-sm">
                      This module has been marked as completed in your training record. 
                      You can now proceed to the next module or return to the Training Hub.
                    </p>
                  </div>
                )}

                <div className="space-y-4">
                  <h3 className="font-semibold">Question Review</h3>
                  {sampleModule.questions.map((question, index) => {
                    const userAnswer = selectedAnswers[question.id];
                    const isCorrect = userAnswer === question.correctAnswer;
                    
                    return (
                      <div key={question.id} className="border rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="mt-1">
                            {isCorrect ? (
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-600" />
                            )}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium mb-2">
                              Question {index + 1}: {question.question}
                            </h4>
                            <div className="space-y-1 text-sm">
                              <div className={isCorrect ? "text-green-600" : "text-red-600"}>
                                Your answer: {question.options[userAnswer]}
                              </div>
                              {!isCorrect && (
                                <div className="text-green-600">
                                  Correct answer: {question.options[question.correctAnswer]}
                                </div>
                              )}
                              <div className="text-muted-foreground">
                                {question.explanation}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex gap-4">
                  <Button onClick={() => navigate('/training-hub')} className="flex-1">
                    Return to Training Hub
                  </Button>
                  {!results.passed && (
                    <Button onClick={restartModule} variant="outline" className="flex-1">
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Retry Module
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <Button 
              variant="outline" 
              onClick={() => navigate('/training-hub')}
              className="mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Training Hub
            </Button>
            <h1 className="text-2xl font-bold mb-2">{sampleModule.title}</h1>
            <p className="text-muted-foreground">{sampleModule.description}</p>
          </div>

          {/* Progress and Timer */}
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <div className="text-sm text-muted-foreground">Progress</div>
                  <Badge variant="outline">
                    {currentQuestionIndex + 1} of {totalQuestions}
                  </Badge>
                </div>
                <Progress value={progress} className="mt-2" />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <div className="text-sm text-muted-foreground">Time Limit</div>
                </div>
                <div className="text-lg font-semibold">{sampleModule.timeLimit} minutes</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Award className="h-4 w-4 text-muted-foreground" />
                  <div className="text-sm text-muted-foreground">Pass Rate</div>
                </div>
                <div className="text-lg font-semibold">{sampleModule.passRate}%</div>
              </CardContent>
            </Card>
          </div>

          {/* Question Card */}
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  Question {currentQuestionIndex + 1}
                </CardTitle>
                <Badge variant="secondary">{currentQuestion.category}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <h2 className="text-xl font-medium">{currentQuestion.question}</h2>
              
              <RadioGroup 
                value={selectedAnswers[currentQuestion.id]?.toString() || ""} 
                onValueChange={handleAnswerSelect}
              >
                {currentQuestion.options.map((option, index) => (
                  <div key={index} className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50">
                    <RadioGroupItem value={index.toString()} id={`option-${index}`} />
                    <Label htmlFor={`option-${index}`} className="flex-1 cursor-pointer">
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex justify-between">
            <Button 
              variant="outline" 
              onClick={previousQuestion}
              disabled={currentQuestionIndex === 0}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Previous
            </Button>
            
            <Button 
              onClick={nextQuestion}
              disabled={!selectedAnswers[currentQuestion.id]}
            >
              {currentQuestionIndex === totalQuestions - 1 ? (
                <>Complete Module</>
              ) : (
                <>
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}